<template>
  <ConfigPageShell
    title="Pre-Processor Configuration"
    subtitle="Configure how this account's inventory feed is processed — either as a group site or shared inventory."
    dense
  >
      <!-- Loading -->
      <div v-if="isLoading" class="d-flex align-center justify-center pa-8">
        <v-progress-circular indeterminate color="primary" size="44" />
        <span class="ml-4 text-body-1">Loading config…</span>
      </div>

      <!-- Error -->
      <v-alert v-if="error" type="error" variant="tonal" class="mb-4" closable @click:close="error = ''">
        {{ error }}
      </v-alert>

      <!-- Content -->
      <div v-if="!isLoading">
        <v-alert
          v-if="productActive === false"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          The <strong>ise_pre_processor</strong> product is inactive or not configured for this account in JAMS.
          All fields are disabled. Activate the product in JAMS to make changes.
        </v-alert>

        <!-- Mode Toggle: only shown when no mode is configured yet -->
        <v-btn-toggle
          v-if="configuredMode === 'none'"
          v-model="selectedMode"
          mandatory
          color="primary"
          variant="outlined"
          density="compact"
          class="mb-4"
          :disabled="isFormDisabled"
        >
          <v-btn value="groupSite" prepend-icon="mdi-source-merge">
            Group Site
          </v-btn>
          <v-btn value="sharedInventory" prepend-icon="mdi-source-fork">
            Shared Inventory
          </v-btn>
        </v-btn-toggle>

        <!-- Mode indicator: shown when a mode is already configured in JAMS -->
        <v-chip
          v-if="configuredMode !== 'none'"
          :prepend-icon="configuredMode === 'sharedInventory' ? 'mdi-source-fork' : 'mdi-source-merge'"
          color="primary"
          variant="tonal"
          class="mb-4"
        >
          {{ configuredMode === 'sharedInventory' ? 'Shared Inventory' : 'Group Site' }}
        </v-chip>

        <!-- ================================================================ -->
        <!-- GROUP SITE MODE -->
        <!-- ================================================================ -->
        <template v-if="selectedMode === 'groupSite'">
          <v-alert type="info" variant="tonal" class="mb-4">
            Each source entry pulls vehicles from another dealer's feed into this site.
            Filters narrow which vehicles are shared (e.g., only used vehicles, specific make/model).
            Changes are saved as a <strong>draft</strong> — click <strong>Publish Changes</strong> to apply.
          </v-alert>

          <!-- Source entries list -->
          <div v-for="(entry, idx) in localSources" :key="idx" class="mb-4">
            <v-card variant="outlined" class="pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-subtitle-2 font-weight-bold">Source {{ idx + 1 }}</div>
                <v-btn
                  icon="mdi-delete-outline"
                  size="small"
                  variant="text"
                  color="error"
                  :disabled="isFormDisabled"
                  @click="removeSource(idx)"
                />
              </div>

              <v-row dense>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="entry.sourceJuid"
                    label="Source JUID"
                    placeholder="e.g., USMTARC4492001"
                    variant="outlined"
                    density="compact"
                    :disabled="isFormDisabled"
                    :rules="[v => !!v?.trim() || 'Source JUID is required']"
                    hide-details="auto"
                    @blur="onSourceJuidBlur(entry)"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="entry.sourceFeed"
                    :items="entry.availableFeeds"
                    item-title="friendlyName"
                    item-value="id"
                    label="Source Feed"
                    placeholder="Select feed"
                    variant="outlined"
                    density="compact"
                    clearable
                    :disabled="isFormDisabled || entry.availableFeeds.length === 0"
                    :loading="entry.feedsLoading"
                    :hint="entry.availableFeeds.length === 0 && !entry.feedsLoading && entry.sourceJuid.trim() ? 'Enter JUID and tab out to load feeds' : ''"
                    persistent-hint
                    hide-details="auto"
                  />
                </v-col>
              </v-row>

              <v-row dense class="mt-1">
                <v-col cols="12">
                  <v-switch
                    v-model="entry.useSourceLocation"
                    label="Use location from source JUID"
                    hint="When enabled, vehicles from this source will use the source account's address instead of this account's address"
                    persistent-hint
                    color="primary"
                    density="compact"
                    :disabled="isFormDisabled"
                    hide-details="auto"
                  />
                </v-col>
              </v-row>

              <div class="text-subtitle-2 font-weight-medium mt-3 mb-2">Filters</div>
              <filter-fields :filter="entry.filter" :disabled="isFormDisabled" />
            </v-card>
          </div>

          <!-- Empty state -->
          <v-card
            v-if="localSources.length === 0"
            variant="outlined"
            class="pa-8 text-center mb-4"
          >
            <v-icon icon="mdi-link-variant-off" size="48" color="grey" class="mb-2" />
            <div class="text-body-1 text-grey">
              No source accounts configured. Click "Add Source" to start sharing vehicles from another account.
            </div>
          </v-card>

          <!-- Actions -->
          <div class="d-flex justify-space-between align-center mt-4">
            <v-btn
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="isFormDisabled"
              @click="addSource"
            >
              Add Source
            </v-btn>

            <v-btn
              color="primary"
              variant="flat"
              :disabled="isFormDisabled || !isDirty"
              :loading="isSaving"
              @click="saveConfig"
            >
              Save
            </v-btn>
          </div>
        </template>

        <!-- ================================================================ -->
        <!-- SHARED INVENTORY MODE -->
        <!-- ================================================================ -->
        <template v-if="selectedMode === 'sharedInventory'">
          <v-alert type="info" variant="tonal" class="mb-4">
            Shared inventory splits a single feed file across multiple accounts using filters.
            Select the <strong>feed source</strong> configured for this account — the
            <strong>pattern</strong> will be auto-filled from JAMS. Then add one or more
            <strong>filters</strong> to select which vehicles belong to this account.
            Vehicles matching <strong>any</strong> filter are included (union).
            Changes are saved as a <strong>draft</strong> — click <strong>Publish Changes</strong> to apply.
          </v-alert>

          <!-- Feed Source + Pattern -->
          <v-card variant="outlined" class="pa-4 mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-3">Shared Feed Settings</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="sharedFeedSource"
                  :items="accountFeeds"
                  item-title="friendlyName"
                  item-value="id"
                  label="Feed Source"
                  placeholder="Select feed source"
                  variant="outlined"
                  density="compact"
                  :disabled="isFormDisabled || accountFeeds.length === 0"
                  :loading="accountFeedsLoading"
                  :rules="[v => !!v || 'Feed source is required']"
                  hide-details="auto"
                  @update:model-value="onFeedSourceChange"
                />
                <div v-if="accountFeedsError && !isLoading" class="text-caption text-error mt-1">
                  Failed to load feeds: {{ accountFeedsError }}
                </div>
                <div v-else-if="accountFeeds.length === 0 && !accountFeedsLoading && !isLoading && !sharedFeedSource" class="text-caption text-warning mt-1">
                  No import feeds with patterns found for this account in JAMS.
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="sharedPattern"
                  label="Feed Pattern"
                  :placeholder="patternPlaceholder"
                  :hint="patternHint"
                  persistent-hint
                  variant="outlined"
                  density="compact"
                  :disabled="isFormDisabled"
                  :rules="[v => !!v?.trim() || 'Pattern is required']"
                  hide-details="auto"
                  readonly
                />
              </v-col>
            </v-row>
          </v-card>

          <!-- Filter entries -->
          <v-alert
            v-if="sharedFeedSource && !hasSharedFilterCapabilities"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            No Shared Inventory filters are currently supported for this feed.
          </v-alert>

          <template v-else>
            <div v-for="(filter, idx) in sharedFilters" :key="idx" class="mb-4">
              <v-card variant="outlined" class="pa-4">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="text-subtitle-2 font-weight-bold">Filter {{ idx + 1 }}</div>
                  <v-btn
                    icon="mdi-delete-outline"
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="isFormDisabled || sharedFilters.length <= 1"
                    @click="removeSharedFilter(idx)"
                  />
                </div>
                <shared-inventory-filter-fields
                  :filter="filter"
                  :capabilities="sharedFilterCapabilities"
                  :keyword-search-hint="sharedKeywordSearchHint"
                  :disabled="isFormDisabled"
                />
              </v-card>
            </div>
          </template>

          <!-- Actions -->
          <div class="d-flex justify-space-between align-center mt-4">
            <v-btn
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="isFormDisabled || !hasSharedFilterCapabilities"
              @click="addSharedFilter"
            >
              Add Filter
            </v-btn>

            <v-btn
              color="primary"
              variant="flat"
              :disabled="isFormDisabled || !isDirty || !hasSharedFilterCapabilities"
              :loading="isSaving"
              @click="saveConfig"
            >
              Save
            </v-btn>
          </div>
        </template>
      </div>
    </ConfigPageShell>
</template>

<script setup lang="ts">
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { ref, computed, watch, reactive, defineComponent, h, type PropType } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import type {
  SharedInventoryFilter,
  SharedInventoryFilterField,
} from '@jazel-auto/ise-contracts'
import {
  fetchGroupSiteConfig,
  saveGroupSiteConfigDraft,
  saveSharedInventoryConfigDraft,
  fetchDealerFeeds,
  type GroupSiteSourceEntry,
  type GroupSiteFilter,
  type ConditionValue,
  type DealerFeedInfo,
  type AccountFeedWithPattern,
} from '@/services/groupSiteConfigService'
import {
  createEmptySharedInventoryFilter,
  hasSharedInventoryFilterCapabilities,
  pruneSharedInventoryFilter,
  sharedInventoryKeywordSearchHint,
  sharedInventoryCapabilities,
  sharedInventoryFilterToApi,
  sharedInventoryFilterToLocal,
  type LocalSharedInventoryFilter,
} from '@/utils/sharedInventoryFilters'
import { makeAlert } from '@/utils'

// ---------------------------------------------------------------------------
// Inline filter fields component (reused by both modes)
// ---------------------------------------------------------------------------

const FilterFields = defineComponent({
  name: 'FilterFields',
  props: {
    filter: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
  },
  setup(props) {
    const conditionOptions = [
      { label: 'New', value: 'new' },
      { label: 'Used', value: 'used' },
      { label: 'CPO (Certified Pre-Owned)', value: 'cpo' },
    ]

    return () =>
      h('div', { class: 'v-row v-row--dense' }, [
        filterCol(4, multiSelectField(props.filter, 'condition', 'Condition', conditionOptions, props.disabled)),
        filterCol(4, textField(props.filter, 'make', 'Make', 'e.g., Ford', props.disabled)),
        filterCol(4, textField(props.filter, 'model', 'Model', 'e.g., F-150', props.disabled)),
        filterCol(4, textField(props.filter, 'trim', 'Trim', 'e.g., XLT', props.disabled)),
        filterCol(4, textField(props.filter, 'bodyType', 'Body Type', 'e.g., SUV', props.disabled)),
        filterCol(4, textField(props.filter, 'drivetrain', 'Drivetrain', 'e.g., AWD', props.disabled)),
        filterCol(4, textField(props.filter, 'fuelType', 'Fuel Type', 'e.g., Gasoline', props.disabled)),
        filterCol(4, textField(props.filter, 'exteriorColor', 'Exterior Color', 'e.g., White', props.disabled)),
        filterCol(4, textField(props.filter, 'interiorColor', 'Interior Color', 'e.g., Black', props.disabled)),
        filterCol(3, numberField(props.filter, 'yearMin', 'Year Min', 'e.g., 2020', props.disabled)),
        filterCol(3, numberField(props.filter, 'yearMax', 'Year Max', 'e.g., 2026', props.disabled)),
        filterCol(3, numberField(props.filter, 'priceMin', 'Price Min', 'e.g., 10000', props.disabled)),
        filterCol(3, numberField(props.filter, 'priceMax', 'Price Max', 'e.g., 50000', props.disabled)),
        filterCol(3, numberField(props.filter, 'mileageMin', 'Mileage Min', 'e.g., 0', props.disabled)),
        filterCol(3, numberField(props.filter, 'mileageMax', 'Mileage Max', 'e.g., 100000', props.disabled)),
        filterCol(4, textField(props.filter, 'keyword', 'Keyword Contains', 'e.g., Galpinized, ROUSH', props.disabled)),
      ])
  },
})

const SharedInventoryFilterFields = defineComponent({
  name: 'SharedInventoryFilterFields',
  props: {
    filter: {
      type: Object as PropType<LocalSharedInventoryFilter>,
      required: true,
    },
    capabilities: {
      type: Array as PropType<readonly SharedInventoryFilterField[]>,
      required: true,
    },
    keywordSearchHint: {
      type: String as PropType<string | null>,
      default: null,
    },
    disabled: { type: Boolean, default: false },
  },
  setup(props) {
    const conditionOptions = [
      { label: 'New', value: 'new' },
      { label: 'Used', value: 'used' },
      { label: 'CPO (Certified Pre-Owned)', value: 'cpo' },
    ]
    const supports = (field: SharedInventoryFilterField) =>
      props.capabilities.includes(field)

    return () => {
      const filter = props.filter as unknown as Record<string, unknown>
      const fields: ReturnType<typeof h>[] = []
      if (supports('condition')) {
        fields.push(filterCol(4, multiSelectField(filter, 'condition', 'Condition', conditionOptions, props.disabled)))
      }
      if (supports('make')) {
        fields.push(filterCol(4, textField(filter, 'make', 'Make', 'e.g., Ford', props.disabled)))
      }
      if (supports('model')) {
        fields.push(filterCol(4, textField(filter, 'model', 'Model', 'e.g., F-150', props.disabled)))
      }
      if (supports('yearMin') && supports('yearMax')) {
        fields.push(filterCol(3, numberField(filter, 'yearMin', 'Year Min', 'e.g., 2020', props.disabled)))
        fields.push(filterCol(3, numberField(filter, 'yearMax', 'Year Max', 'e.g., 2026', props.disabled)))
      }
      if (supports('keyword')) {
        fields.push(filterCol(
          4,
          sharedInventoryKeywordTextField(
            filter,
            props.disabled,
            props.keywordSearchHint,
          ),
        ))
      }
      if (supports('accountName')) {
        fields.push(filterCol(4, textField(filter, 'accountName', 'Account Name', 'e.g., Dealer Name', props.disabled)))
      }
      return h('div', { class: 'v-row v-row--dense' }, fields)
    }
  },
})

// Render helpers for FilterFields
import { VCol } from 'vuetify/lib/components/VGrid/index.mjs'
import { VTextField } from 'vuetify/lib/components/VTextField/index.mjs'
import { VSelect } from 'vuetify/lib/components/VSelect/index.mjs'

function filterCol(md: number, child: ReturnType<typeof h>) {
  return h(VCol, { cols: 12, sm: 6, md }, () => child)
}

function textField(filter: Record<string, unknown>, key: string, label: string, placeholder: string, disabled: boolean) {
  return h(VTextField, {
    modelValue: filter[key] ?? '',
    'onUpdate:modelValue': (v: string) => { filter[key] = v },
    label, placeholder, variant: 'outlined', density: 'compact', clearable, disabled, hideDetails: true,
  })
}

/** Shared Inventory-only Keyword field with contract-derived feed-column hint. */
function sharedInventoryKeywordTextField(
  filter: Record<string, unknown>,
  disabled: boolean,
  keywordSearchHint: string | null,
) {
  return h(VTextField, {
    modelValue: filter.keyword ?? '',
    'onUpdate:modelValue': (v: string) => { filter.keyword = v },
    label: 'Keyword Contains',
    placeholder: 'e.g., Galpinized, ROUSH',
    variant: 'outlined',
    density: 'compact',
    clearable,
    disabled,
    ...(keywordSearchHint
      ? {
        hint: keywordSearchHint,
        persistentHint: true,
        hideDetails: false,
      }
      : { hideDetails: true }),
  })
}

function numberField(filter: Record<string, unknown>, key: string, label: string, placeholder: string, disabled: boolean) {
  return h(VTextField, {
    modelValue: filter[key] ?? null,
    'onUpdate:modelValue': (v: unknown) => { filter[key] = v === '' || v === undefined ? null : Number(v) },
    label, placeholder, type: 'number', variant: 'outlined', density: 'compact', clearable, disabled, hideDetails: true,
  })
}

function multiSelectField(filter: Record<string, unknown>, key: string, label: string, items: { label: string; value: string }[], disabled: boolean) {
  return h(VSelect, {
    modelValue: filter[key] ?? [],
    'onUpdate:modelValue': (v: string[] | null) => { filter[key] = v && v.length > 0 ? v : [] },
    items, itemTitle: 'label', itemValue: 'value',
    label, variant: 'outlined', density: 'compact', clearable, disabled, hideDetails: true,
    multiple: true, chips: true, closableChips: true,
  })
}

const clearable = true

// ---------------------------------------------------------------------------
// Stores & config
// ---------------------------------------------------------------------------

const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()
const appConfig = useJazelAppConfig()
const iseBackendUrl = import.meta.env.VITE_ISE_BACKEND_URL

const isLoading = ref(false)
const isSaving = ref(false)
const error = ref('')
const productActive = ref<boolean | null>(null)

const isFormDisabled = computed(() => isSaving.value || productActive.value === false)

// ---------------------------------------------------------------------------
// Mode
// ---------------------------------------------------------------------------

const selectedMode = ref<'groupSite' | 'sharedInventory'>('groupSite')
const configuredMode = ref<'groupSite' | 'sharedInventory' | 'none'>('none')
const jamsPublishedConfig = ref<Record<string, unknown> | null>(null)

// ---------------------------------------------------------------------------
// Group Site state
// ---------------------------------------------------------------------------

interface LocalSourceEntry {
  sourceJuid: string
  sourceFeed: string
  useSourceLocation: boolean
  availableFeeds: DealerFeedInfo[]
  feedsLoading: boolean
  filter: LocalFilter
}

interface LocalFilter {
  condition: ConditionValue[]
  make: string
  model: string
  trim: string
  yearMin: number | null
  yearMax: number | null
  bodyType: string
  drivetrain: string
  fuelType: string
  exteriorColor: string
  interiorColor: string
  priceMin: number | null
  priceMax: number | null
  mileageMin: number | null
  mileageMax: number | null
  keyword: string
}

const EMPTY_FILTER: LocalFilter = {
  condition: [],
  make: '',
  model: '',
  trim: '',
  yearMin: null,
  yearMax: null,
  bodyType: '',
  drivetrain: '',
  fuelType: '',
  exteriorColor: '',
  interiorColor: '',
  priceMin: null,
  priceMax: null,
  mileageMin: null,
  mileageMax: null,
  keyword: '',
}

const localSources = ref<LocalSourceEntry[]>([])

// ---------------------------------------------------------------------------
// Shared Inventory state
// ---------------------------------------------------------------------------

const sharedPattern = ref('')
const sharedFeedSource = ref('')
const accountFeeds = ref<AccountFeedWithPattern[]>([])
const accountFeedsLoading = ref(false)
const accountFeedsError = ref('')
const sharedFilters = ref<LocalSharedInventoryFilter[]>([
  createEmptySharedInventoryFilter(),
])
const sharedFilterCapabilities = computed(() =>
  sharedInventoryCapabilities(sharedFeedSource.value),
)
const hasSharedFilterCapabilities = computed(
  () => hasSharedInventoryFilterCapabilities(sharedFeedSource.value),
)
const sharedKeywordSearchHint = computed(() =>
  sharedInventoryKeywordSearchHint(sharedFeedSource.value),
)

// ---------------------------------------------------------------------------
// Dirty tracking
// ---------------------------------------------------------------------------

const persistedSnapshot = ref('')

const STRING_FILTER_KEYS = ['make', 'model', 'trim', 'bodyType', 'drivetrain', 'fuelType', 'exteriorColor', 'interiorColor', 'keyword'] as const
const NUMBER_FILTER_KEYS = ['yearMin', 'yearMax', 'priceMin', 'priceMax', 'mileageMin', 'mileageMax'] as const

function filterToApi(f: LocalFilter): GroupSiteFilter {
  const filter: GroupSiteFilter = {}
  if (f.condition && f.condition.length > 0) filter.condition = [...f.condition]
  for (const key of STRING_FILTER_KEYS) {
    const val = f[key]
    if (typeof val === 'string' && val.trim()) {
      (filter as Record<string, unknown>)[key] = val.trim()
    }
  }
  for (const key of NUMBER_FILTER_KEYS) {
    const val = f[key]
    if (val != null && typeof val === 'number') {
      (filter as Record<string, unknown>)[key] = val
    }
  }
  return filter
}

function toApiEntry(entry: LocalSourceEntry): GroupSiteSourceEntry {
  const result: GroupSiteSourceEntry = { sourceJuid: entry.sourceJuid.trim(), filter: filterToApi(entry.filter) }
  if (entry.sourceFeed?.trim()) result.sourceFeed = entry.sourceFeed.trim()
  if (entry.useSourceLocation) result.useSourceLocation = true
  return result
}

function currentSnapshot(): string {
  if (selectedMode.value === 'sharedInventory') {
    return JSON.stringify({
      mode: 'sharedInventory',
      feedSource: sharedFeedSource.value.trim(),
      pattern: sharedPattern.value.trim(),
      filters: sharedFilters.value.map((filter) =>
        sharedInventoryFilterToApi(filter, sharedFeedSource.value),
      ),
    })
  }
  return JSON.stringify({
    mode: 'groupSite',
    sources: localSources.value.map(toApiEntry),
  })
}

const isDirty = computed(() => currentSnapshot() !== persistedSnapshot.value)

// ---------------------------------------------------------------------------
// Group Site helpers
// ---------------------------------------------------------------------------

function normalizeConditionFromApi(raw: unknown): ConditionValue[] {
  if (Array.isArray(raw)) return raw.filter((v): v is ConditionValue => v === 'new' || v === 'used' || v === 'cpo')
  if (raw === 'new' || raw === 'used' || raw === 'cpo') return [raw]
  return []
}

function toLocalEntry(entry: GroupSiteSourceEntry): LocalSourceEntry {
  return reactive({
    sourceJuid: entry.sourceJuid || '',
    sourceFeed: entry.sourceFeed || '',
    useSourceLocation: entry.useSourceLocation ?? false,
    availableFeeds: [],
    feedsLoading: false,
    filter: {
      ...EMPTY_FILTER,
      condition: normalizeConditionFromApi(entry.filter?.condition),
      make: entry.filter?.make ?? '',
      model: entry.filter?.model ?? '',
      trim: entry.filter?.trim ?? '',
      yearMin: entry.filter?.yearMin ?? null,
      yearMax: entry.filter?.yearMax ?? null,
      bodyType: entry.filter?.bodyType ?? '',
      drivetrain: entry.filter?.drivetrain ?? '',
      fuelType: entry.filter?.fuelType ?? '',
      exteriorColor: entry.filter?.exteriorColor ?? '',
      interiorColor: entry.filter?.interiorColor ?? '',
      priceMin: entry.filter?.priceMin ?? null,
      priceMax: entry.filter?.priceMax ?? null,
      mileageMin: entry.filter?.mileageMin ?? null,
      mileageMax: entry.filter?.mileageMax ?? null,
      keyword: entry.filter?.keyword ?? '',
    },
  })
}

function addSource() {
  localSources.value.push(
    reactive({
      sourceJuid: '',
      sourceFeed: '',
      useSourceLocation: false,
      availableFeeds: [],
      feedsLoading: false,
      filter: { ...EMPTY_FILTER },
    }),
  )
}

function removeSource(idx: number) {
  localSources.value.splice(idx, 1)
}

async function loadFeedsForSource(entry: LocalSourceEntry) {
  const juid = entry.sourceJuid.trim()
  if (!juid) {
    entry.availableFeeds = []
    entry.sourceFeed = ''
    return
  }
  const endpoint = String(iseBackendUrl || '')
  const token = String(accountSettings.sessionToken || '').trim()
  if (!endpoint || !token) return

  entry.feedsLoading = true
  try {
    const feeds = await fetchDealerFeeds(endpoint, juid, { token })
    entry.availableFeeds = feeds
    if (entry.sourceFeed && !feeds.some((f) => f.id === entry.sourceFeed)) {
      entry.sourceFeed = ''
    }
  } catch {
    entry.availableFeeds = []
  } finally {
    entry.feedsLoading = false
  }
}

function onSourceJuidBlur(entry: LocalSourceEntry) {
  loadFeedsForSource(entry)
}

// ---------------------------------------------------------------------------
// Shared Inventory feed source helpers
// ---------------------------------------------------------------------------

const FEED_PATTERN_HINTS: Record<string, { placeholder: string; hint: string }> = {
  import_vauto: {
    placeholder: 'e.g., MP90684',
    hint: 'vAuto files: {pattern}_{timestamp}.txt — auto-filled from JAMS config',
  },
  import_homenet: {
    placeholder: 'e.g., hacienda',
    hint: 'Homenet files: {pattern}_full_{timestamp}.txt — auto-filled from JAMS config',
  },
  import_fdds1: {
    placeholder: 'e.g., dealer123',
    hint: 'FDDS1 files: {pattern}_{timestamp}.txt — auto-filled from JAMS config',
  },
  import_icc: {
    placeholder: 'e.g., GalpinFord-Icc',
    hint: 'ICC files: {pattern}_{timestamp}.txt — auto-filled from JAMS config',
  },
  import_locate: {
    placeholder: 'e.g., dealer_locate',
    hint: 'Locate files: {pattern}_{timestamp}.json — auto-filled from JAMS config',
  },
}

const patternPlaceholder = computed(() => {
  const feedId = sharedFeedSource.value
  return FEED_PATTERN_HINTS[feedId]?.placeholder ?? 'Select a feed source first'
})

const patternHint = computed(() => {
  const feedId = sharedFeedSource.value
  return FEED_PATTERN_HINTS[feedId]?.hint ?? 'The filename pattern that identifies the shared feed file on FTP'
})

function onFeedSourceChange(feedId: string | null) {
  sharedFilters.value = sharedFilters.value.map((filter) =>
    reactive(pruneSharedInventoryFilter(filter, feedId)),
  )
  if (!feedId) {
    sharedPattern.value = ''
    return
  }
  const feed = accountFeeds.value.find(f => f.id === feedId)
  if (feed?.pattern) {
    sharedPattern.value = feed.pattern
  }
}

// ---------------------------------------------------------------------------
// Shared Inventory filter helpers
// ---------------------------------------------------------------------------

function addSharedFilter() {
  sharedFilters.value.push(reactive(createEmptySharedInventoryFilter()))
}

function removeSharedFilter(idx: number) {
  if (sharedFilters.value.length > 1) {
    sharedFilters.value.splice(idx, 1)
  }
}

// ---------------------------------------------------------------------------
// Load & Save
// ---------------------------------------------------------------------------

async function checkProductStatus() {
  const jamsBase = String(appConfig.jamsBackendUrl || '').replace(/\/+$/g, '')
  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()
  if (!jamsBase || !accountId || !token) {
    productActive.value = null
    return
  }
  try {
    const resp = await fetch(
      `${jamsBase}/account/products/${encodeURIComponent(accountId)}/ise_pre_processor`,
      { headers: { authorization: `Bearer ${token}` } },
    )
    if (!resp.ok) {
      productActive.value = false
      return
    }
    const json: Record<string, unknown> = await resp.json().catch(() => ({}))
    const cfg = (json?.config ?? json) as Record<string, unknown>
    productActive.value = cfg?.isActive === true || cfg?.Enable === true
    jamsPublishedConfig.value = cfg ?? null

    // Derive the configured mode directly from JAMS (source of truth)
    if (cfg?.sharedInventory === true) {
      configuredMode.value = 'sharedInventory'
    } else if (cfg?.groupSite === true || Array.isArray(cfg?.sources)) {
      configuredMode.value = 'groupSite'
    } else {
      configuredMode.value = 'none'
    }
  } catch {
    productActive.value = false
  }
}

// Retained unchanged with the Group Site helpers; Shared Inventory uses its isolated converter.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function apiFilterToLocal(f?: GroupSiteFilter): LocalFilter {
  return {
    ...EMPTY_FILTER,
    condition: normalizeConditionFromApi(f?.condition),
    make: f?.make ?? '',
    model: f?.model ?? '',
    trim: f?.trim ?? '',
    yearMin: f?.yearMin ?? null,
    yearMax: f?.yearMax ?? null,
    bodyType: f?.bodyType ?? '',
    drivetrain: f?.drivetrain ?? '',
    fuelType: f?.fuelType ?? '',
    exteriorColor: f?.exteriorColor ?? '',
    interiorColor: f?.interiorColor ?? '',
    priceMin: f?.priceMin ?? null,
    priceMax: f?.priceMax ?? null,
    mileageMin: f?.mileageMin ?? null,
    mileageMax: f?.mileageMax ?? null,
    keyword: f?.keyword ?? '',
  }
}

async function loadConfig() {
  const endpoint = String(iseBackendUrl || '')
  const jazelId = String(accountSettings.accountId || '')
  const token = String(accountSettings.sessionToken || '')
  if (!endpoint || !jazelId || !token) return

  isLoading.value = true
  error.value = ''
  try {
    await checkProductStatus()
    const { config: res, accountFeeds: feeds, accountFeedsError: feedsErr } = await fetchGroupSiteConfig(endpoint, jazelId, {
      includeDraft: true,
      includeFeeds: true,
      token,
    })

    if (feeds) accountFeeds.value = feeds
    accountFeedsError.value = feedsErr ?? ''

    const effectiveMode = res.mode !== 'none' ? res.mode : configuredMode.value

    if (effectiveMode === 'sharedInventory') {
      selectedMode.value = 'sharedInventory'
      const jams = jamsPublishedConfig.value

      sharedFeedSource.value = res.feedSource
        ?? (typeof jams?.feedSource === 'string' ? (jams.feedSource as string) : '')

      sharedPattern.value = res.pattern
        ?? (typeof jams?.pattern === 'string' ? jams.pattern : '')

      let rawFilters: SharedInventoryFilter[] | undefined = res.filters
      if (!rawFilters || rawFilters.length === 0) {
        if (Array.isArray(jams?.filters)) {
          rawFilters = jams.filters as SharedInventoryFilter[]
        } else if (jams?.filter && typeof jams.filter === 'object') {
          rawFilters = [jams.filter as SharedInventoryFilter]
        }
      }

      sharedFilters.value = rawFilters && rawFilters.length > 0
        ? rawFilters.map(f => reactive(
            sharedInventoryFilterToLocal(f, sharedFeedSource.value),
          ))
        : [reactive(createEmptySharedInventoryFilter())]
      localSources.value = []
    } else {
      selectedMode.value = 'groupSite'
      const entries = Array.isArray(res.sources) ? res.sources : []
      localSources.value = entries.map(toLocalEntry)
      sharedPattern.value = ''
      sharedFeedSource.value = ''
      sharedFilters.value = [reactive(createEmptySharedInventoryFilter())]
      for (const entry of localSources.value) {
        if (entry.sourceJuid.trim()) loadFeedsForSource(entry)
      }
    }

    persistedSnapshot.value = currentSnapshot()
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to load config'
  } finally {
    isLoading.value = false
  }
}

async function saveConfig() {
  const endpoint = String(iseBackendUrl || '')
  const jazelId = String(accountSettings.accountId || '')
  const token = String(accountSettings.sessionToken || '')
  if (!endpoint || !jazelId || !token) return

  isSaving.value = true
  error.value = ''
  try {
    if (selectedMode.value === 'sharedInventory') {
      if (!sharedFeedSource.value.trim()) {
        error.value = 'Feed source is required for shared inventory.'
        isSaving.value = false
        return
      }
      if (!sharedPattern.value.trim()) {
        error.value = 'Pattern is required for shared inventory.'
        isSaving.value = false
        return
      }
      if (!hasSharedFilterCapabilities.value) {
        error.value = 'No Shared Inventory filters are currently supported for this feed.'
        isSaving.value = false
        return
      }
      await saveSharedInventoryConfigDraft(endpoint, jazelId, {
        pattern: sharedPattern.value.trim(),
        feedSource: sharedFeedSource.value.trim(),
        filters: sharedFilters.value.map((filter) =>
          sharedInventoryFilterToApi(filter, sharedFeedSource.value),
        ),
      }, { token })
      makeAlert('Shared inventory config saved as draft. Click "Publish Changes" to apply.', 'success')
    } else {
      const validSources = localSources.value.filter((e) => e.sourceJuid.trim())
      if (localSources.value.length > 0 && validSources.length === 0) {
        error.value = 'Each source must have a Source JUID.'
        isSaving.value = false
        return
      }
      const apiSources = validSources.map(toApiEntry)
      await saveGroupSiteConfigDraft(endpoint, jazelId, apiSources, { token })
      makeAlert('Group site config saved as draft. Click "Publish Changes" to apply.', 'success')
    }

    configuredMode.value = selectedMode.value
    persistedSnapshot.value = currentSnapshot()
    await publishStore.refreshUnpublishedChangesSummary()
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to save config'
  } finally {
    isSaving.value = false
  }
}

watch(
  () => [accountSettings.accountId, accountSettings.sessionToken],
  () => {
    loadConfig()
  },
  { immediate: true },
)
</script>
